import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { dartThemes } from '../dartThemes';
import { logError } from '../utils/log';

export const MemberProfilePage = ({ pageProps, navigate }) => {
    const memberId = pageProps?.memberId;
    const [member, setMember] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMember = async () => {
            if (!memberId) { setLoading(false); return; }
            try {
                const userDoc = await getDoc(doc(db, 'users', memberId));
                if (userDoc.exists()) {
                    setMember({ id: userDoc.id, ...userDoc.data() });
                }
            } catch (e) {
                logError('Failed to load member profile', e);
            } finally {
                setLoading(false);
            }
        };
        fetchMember();
    }, [memberId]);

    // Removed phone display helper as contact info is no longer shown

    if (loading) return <div className="text-center text-muted">Loading member profile...</div>;
    if (!member) return <div className="text-center text-danger">Member not found.</div>;

    return (
        <div className="space-y-6">
            <div className="flex">
                <button
                    type="button"
                    onClick={() => navigate && navigate('Members')}
                    className="btn btn-secondary inline-flex items-center text-sm"
                >
                    &larr; Back to Members
                </button>
            </div>
            
            <h2 className="page-title heading-primary-1">Member Profile</h2>
            
            <div className="space-y-6">
                {/* Header card */}
                <div className="card rounded-lg p-6 flex flex-col sm:flex-row items-center gap-4">
                    <div className="surface-low w-20 h-20 rounded-full flex items-center justify-center overflow-hidden shadow-inner">
                        {dartThemes[member.profileImage] || dartThemes['default']}
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                        <p className="text-2xl font-semibold text-strong">
                            {member.nickname || `${member.firstName} ${member.lastName}`}
                        </p>
                        <p className="text-sm text-muted mt-1">Role: {member.role || 'N/A'}</p>
                    </div>
                </div>

                {/* Member Details Card */}
                <div className="card rounded-lg p-6">
                    <h3 className="text-2xl font-semibold heading-primary-1 mb-4">Member Details</h3>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                        <div>
                            <dt className="label">Name</dt>
                            <dd className="text-sm text-strong">{member.firstName} {member.lastName}</dd>
                        </div>
                        {member.currentTeam && (
                            <div>
                                <dt className="label">Current Team</dt>
                                <dd className="text-sm text-strong">{member.currentTeam.name || 'N/A'}</dd>
                            </div>
                        )}
                    </dl>
                </div>

                {/* Dart Information Card */}
                {(member.dartInfo?.brand || member.dartInfo?.range || member.dartInfo?.weight || member.dartInfo?.flightShaft || member.dartInfo?.points) && (
                    <div className="card rounded-lg p-6">
                        <h3 className="text-2xl font-semibold heading-primary-1 mb-4">Dart Information</h3>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                            {member.dartInfo?.brand && (
                                <div>
                                    <dt className="label">Brand</dt>
                                    <dd className="text-sm text-strong">{member.dartInfo.brand}</dd>
                                </div>
                            )}
                            {member.dartInfo?.range && (
                                <div>
                                    <dt className="label">Range</dt>
                                    <dd className="text-sm text-strong">{member.dartInfo.range}</dd>
                                </div>
                            )}
                            {member.dartInfo?.weight && (
                                <div>
                                    <dt className="label">Weight</dt>
                                    <dd className="text-sm text-strong">{member.dartInfo.weight}</dd>
                                </div>
                            )}
                            {member.dartInfo?.flightShaft && (
                                <div>
                                    <dt className="label">Flight/Shaft</dt>
                                    <dd className="text-sm text-strong">{member.dartInfo.flightShaft}</dd>
                                </div>
                            )}
                            {member.dartInfo?.points && (
                                <div>
                                    <dt className="label">Points</dt>
                                    <dd className="text-sm text-strong">{member.dartInfo.points}</dd>
                                </div>
                            )}
                        </dl>
                    </div>
                )}

                {/* Home Setup Card */}
                {(member.homeSetup?.dartboard || member.homeSetup?.light || member.homeSetup?.surround || member.homeSetup?.autoScoringSystem) && (
                    <div className="card rounded-lg p-6">
                        <h3 className="text-2xl font-semibold heading-primary-1 mb-4">Home Setup Information</h3>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                            {member.homeSetup?.dartboard && (
                                <div>
                                    <dt className="label">Dartboard</dt>
                                    <dd className="text-sm text-strong">{member.homeSetup.dartboard}</dd>
                                </div>
                            )}
                            {member.homeSetup?.light && (
                                <div>
                                    <dt className="label">Light</dt>
                                    <dd className="text-sm text-strong">{member.homeSetup.light}</dd>
                                </div>
                            )}
                            {member.homeSetup?.surround && (
                                <div>
                                    <dt className="label">Surround</dt>
                                    <dd className="text-sm text-strong">{member.homeSetup.surround}</dd>
                                </div>
                            )}
                            {member.homeSetup?.autoScoringSystem && (
                                <div>
                                    <dt className="label">Auto Scoring System</dt>
                                    <dd className="text-sm text-strong">{member.homeSetup.autoScoringSystem}</dd>
                                </div>
                            )}
                        </dl>
                    </div>
                )}
            </div>
        </div>
    );
};
